import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/language/LanguageProvider";
import { TournamentProvider } from "@/contexts/tournament/TournamentProvider";
import { SupabaseEventProvider } from "@/contexts/event/SupabaseEventProvider";
import { AuthProvider } from "@/contexts/auth/AuthProvider";
import { PenaltyProvider } from "@/contexts/penalty/PenaltyProvider";
import { MessageProvider } from "@/contexts/message/MessageProvider";
import { GroupProvider } from "@/contexts/GroupContext";
import { NotificationProvider } from "@/contexts/notification/NotificationProvider";
import { NotificationDataProvider } from "@/contexts/notification/NotificationDataProvider";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import LandingPage from "./pages/LandingPage";
import Profile from "./pages/Profile";
import TournamentDetail from "./pages/TournamentDetail";
import TournamentResults from "./pages/TournamentResults";
import EventDetail from "./pages/EventDetail";
import AmericanoTournamentPage from "./pages/AmericanoTournamentPage";
import MexicanoTournamentPage from "./pages/MexicanoTournamentPage";
import PastTournaments from "./pages/PastTournaments";
import { AppLayout } from "@/components/layout/AppLayout";
import Dashboard from "./pages/Dashboard";
import Calendar from "./pages/Calendar";
import Groups from "./pages/Groups";
import Members from "./pages/Members";
import { ProtectedAdminRoute } from "@/components/admin/ProtectedAdminRoute";
import Tournaments from "./pages/Tournaments";
import Notifications from "./pages/Notifications";
import Messages from "./pages/Messages";
import Rankings from "./pages/Rankings";
import { lazy, Suspense } from "react";

// Lazy load admin page to reduce initial bundle size
const Admin = lazy(() => import("./pages/Admin"));

const queryClient = new QueryClient();

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useLanguage();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage />;
  }

  return (
    <AppLayout>
      <ErrorBoundary>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/subgroups" element={<Groups />} />
        <Route path="/members" element={<Members />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/tournaments" element={<Tournaments />} />
        <Route path="/past-tournaments" element={<PastTournaments />} />
        <Route path="/tournaments/rankings" element={<Rankings />} />
        <Route path="/tournaments/:id" element={<TournamentResults />} />
        <Route path="/americano" element={<AmericanoTournamentPage />} />
        <Route path="/mexicano" element={<MexicanoTournamentPage />} />
        <Route path="/tournament/:id" element={<TournamentDetail />} />
        <Route path="/event/:id" element={<EventDetail />} />
        <Route path="/admin" element={
          <ProtectedAdminRoute fallback={<Navigate to="/" replace />}> 
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-muted-foreground">{t("common.loading")}</p>
                </div>
              </div>
            }>
              <Admin />
            </Suspense>
          </ProtectedAdminRoute>
        } />
        <Route path="*" element={<Dashboard />} />
      </Routes>
      </ErrorBoundary>
    </AppLayout>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <AuthProvider>
        <PenaltyProvider>
          <GroupProvider>
            <SupabaseEventProvider>
              <TournamentProvider>
                <MessageProvider>
                  <NotificationProvider>
                  <NotificationDataProvider>
                    <TooltipProvider>
                      <Toaster />
                      <Sonner />
                      <BrowserRouter>
                        <AppContent />
                      </BrowserRouter>
                    </TooltipProvider>
                  </NotificationDataProvider>
                  </NotificationProvider>
                  </MessageProvider>
                </TournamentProvider>
              </SupabaseEventProvider>
            </GroupProvider>
          </PenaltyProvider>
      </AuthProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
